import { defineStore } from 'pinia';
import { axiosInstance } from '@/lib/axios';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';

export const useMyStore = defineStore('store', {
  id: 'app',
  state: () => ({
    user: {},
    editTransaction: {},
    transactionTypes: ['Name', 'Amount'],
    transactionNameComparisons: ['equals', 'contains'],
    transactionAmountComparisons: ['equals', 'Greater Than', 'Less than'],
    editCategory: {},
    addSubcategoryToEditCategory: false,
    accountsData: [],
    userLoggedIn: localStorage.getItem('userLoggedIn') === 'true',
    // userLoggedIn: localStorage.getItem('userLoggedIn') === 'false',
    categories: [],
    categoryRules: [],
    subcategoriesOnNewCategory: [],
    linkToken: '',
    transactionsList: [],
    accountsList: [],
    selectedAccount: {},
    editCategoryIsSubcategory: false,
    editCategoryBudget: {},
    editCategoryMonth: '',
    editCategoryYear: '',
    error: '',
    userInfo: JSON.parse(localStorage.getItem('userInfo')) || {},
    userDataLoaded: false,
    newUserFlowComplete: false,
    isUserOver18: true,
    authToken: null,
    authInitialized: false,
    filters: {
      searchText: '',
      parentCategory: '',
      selectedSubcategory: '',
      selectedDate: 'Current Month',
      startDate: '',
      endDate: '',
      customStartDate: '',
      customEndDate: '',
      showOnlyUncategorized: false,
    },
    isLoggingOut: false,
    plaidNeedsAttention: {},
    plaidNeedsAttentionInstitution: null,
  }),
  getters: {
    darkTheme() {
      return this.user.settings?.darkMode ?? false;
    },
  },
  actions: {
    setAuthInitialized(value) {
      this.authInitialized = value;
    },
    setAuthToken(token) {
      this.authToken = token;
    },
    setUserAgeOver18(birthdate) {
      if (!birthdate || new Date(birthdate).getFullYear() <= 1) {
        this.isUserOver18 = false;
        return;
      }

      const birthDate = new Date(birthdate);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      this.isUserOver18 = age >= 18;
    },
    updateNewUserFlowComplete(value) {
      this.newUserFlowComplete = value;
    },
    updateEditTransaction(data) {
      this.editTransaction = data;
    },
    updateEditCategory(category, isSubcategory) {
      this.editCategory = category;
      this.editCategoryIsSubcategory = isSubcategory;
    },
    updateAddSubcategoryToEditCategory(value) {
      this.addSubcategoryToEditCategory = value;
    },
    updateAccountsData(data) {
      this.accountsData = data;
    },
    updateSubcategoriesOnNewCategory(subcategory) {
      this.subcategoriesOnNewCategory.push(subcategory);
    },
    resetSubcategoriesOnNewCategory() {
      this.subcategoriesOnNewCategory = [];
    },
    editAccount(account) {
      this.selectedAccount = account;
    },
    setError(message) {
      this.error = message;
    },
    setUserDataLoaded(value) {
      this.userDataLoaded = value;
    },
    updateEditCategoryBudget(budget, month, year) {
      return new Promise((resolve, reject) => {
        try {
          this.editCategoryBudget = budget;
          this.editCategoryMonth = month;
          this.editCategoryYear = year;
          resolve('Budget updated successfully');
        } catch (error) {
          console.error('Error in updating edit category budget ', error);
          this.setError('Error in updating edit category budget');
          reject('Failed to update budget: ' + error);
        }
      });
    },
    async fetchAuth0UserInfo() {
      try {
        const response = await fetch(
          `https://${import.meta.env.VITE_AUTH0_DOMAIN}/userinfo`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }
        const userInfo = await response.json();
        return userInfo;
      } catch (error) {
        console.error('Error fetching Auth0 user info:', error);
        this.setError('Failed to fetch Auth0 user info');
        throw error;
      }
    },
    createMonthlyBudget(month, year) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('MonthlyBudget/CreateMonthlyBudget', {
            Month: month,
            Year: year,
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error creating monthly budget', error);
            this.setError('Error creating monthly budget');
            reject(error);
          });
      });
    },
    createAnnualBudget(year) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('AnnualBudget/CreateAnnualBudget', {
            Year: year,
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error creating annual budget', error);
            this.setError('Error creating annual budget');
            reject(error);
          });
      });
    },
    getMonthlyBudgetDataForAllCategories(month, year) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get(`MonthlyBudget/GetMonthlyBudgetDataForAllCategories`, {
            params: {
              Month: month,
              Year: year,
            },
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error(
              'Error getting monthly budget data for categories',
              error
            );
            this.setError('Error getting monthly budget data for categories');
            reject(error);
          });
      });
    },
    getAnnualBudgetDataForAllCategories(year) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get(`AnnualBudget/GetAnnualBudgetDataForAllCategories`, {
            params: {
              Year: year,
            },
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error(
              'Error getting annual budget data for categories',
              error
            );
            this.setError('Error getting annual budget data for categories');
            reject(error);
          });
      });
    },
    saveCategoryBudgetDataForMonth(updatedCategoryBudget) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(
            `MonthlyBudget/SaveCategoryBudgetDataForMonth`,
            updatedCategoryBudget
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error(
              'Error saving category budget data for the month',
              error
            );
            this.setError('Error saving category budget data for the month');
            reject(error);
          });
      });
    },
    saveSubcategoryBudgetDataForMonth(updatedSubcategoryBudget) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(
            `MonthlyBudget/SaveSubcategoryBudgetDataForMonth`,
            updatedSubcategoryBudget
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error(
              'Error saving subcategory budget data for the month',
              error
            );
            this.setError('Error saving subcategory budget data for the month');
            reject(error);
          });
      });
    },
    getAllCategories() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Category/GetAllCategories')
          .then((response) => {
            this.categories = response.data;
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting categories', error);
            this.setError('Error getting categories');
            reject(error);
          });
      });
    },
    updateUserLoggedIn(value) {
      this.userLoggedIn = value;
      localStorage.setItem('userLoggedIn', value);
    },
    setUserInfo(info) {
      this.userInfo = info;
      this.userDataLoaded = true;
    },
    // getCategoryRules({ categoryID, subcategoryID }) {
    //   console.log('at store call: ' + categoryID + subcategoryID);
    //   return new Promise((resolve, reject) => {
    //     axiosInstance
    //       .get(`Rule/GetCategoryRules`, {
    //         params: {
    //           categoryID: categoryID,
    //           subcategoryID: subcategoryID,
    //         },
    //       })
    //       .then((response) => {
    //         this.categoryRules = response.data;
    //         resolve(response);
    //       })
    //       .catch((error) => {
    //         console.error('Error getting category rules', error);
    //         this.setError('Error getting category rules');
    //         reject(error);
    //       });
    //   });
    // },
    getCategoryRules({ categoryID, subcategoryID }) {
      const params = { categoryID };

      if (Number.isInteger(subcategoryID)) params.subcategoryID = subcategoryID;

      return new Promise((resolve, reject) => {
        axiosInstance
          .get(`Rule/GetCategoryRules`, {
            params,
          })
          .then((response) => {
            this.categoryRules = response.data;
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting category rules', error);
            this.setError('Error getting category rules');
            reject(error);
          });
      });
    },
    getCategoryRulesForTransaction({ categoryID, subcategoryID }) {
      const params = { categoryID };
      if (Number.isInteger(subcategoryID)) params.subcategoryID = subcategoryID;

      return new Promise((resolve, reject) => {
        axiosInstance
          .get(`Rule/GetCategoryRules`, { params })
          .then((response) => resolve(response.data))
          .catch((error) => {
            console.error(
              'Error getting category rules for transaction',
              error
            );
            reject(error);
          });
      });
    },
    saveCategory(categoryDetails) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Category/SaveCategory`, categoryDetails)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving the category', error);
            this.setError('Error saving the category');
            reject(error);
          });
      });
    },
    saveSubcategory(subcategoryDetails) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Subcategory/SaveSubcategory`, subcategoryDetails)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving subcategory', error);
            this.setError('Error saving subcategory');
            reject(error);
          });
      });
    },
    saveSuggestedCategories(categoryWithSubcategories) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(
            `Onboarding/SeedDefaultCategoriesAndRules`,
            categoryWithSubcategories
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving suggested categories', error);
            this.seterror('Error saving suggested categories');
            reject(error);
          });
      });
    },
    deleteCategory(categoryDetails) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .delete(`Category/DeleteCategory`, { data: categoryDetails })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error deleting the category', error);
            this.setError('Error deleting the category');
            reject(error);
          });
      });
    },
    deleteSubcategory(categoryDetails) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .delete(`Subcategory/DeleteSubcategory`, { data: categoryDetails })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error deleting the subcategory', error);
            this.setError('Error deleting the subcategory');
            reject(error);
          });
      });
    },
    saveCategoryRules(rules) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Rule/SaveCategoryRules`, rules)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving category rules', error);
            this.setError('Error saving category rules');
            reject(error);
          });
      });
    },
    createLinkToken() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Plaid/CreateLinkToken')
          .then((response) => {
            resolve(response);
            this.linkToken = response.data;
          })
          .catch((error) => {
            console.error('Error creating link token', error);
            this.setError('Error creating link token');
            reject(error);
          });
      });
    },
    // updateLinkToken(accessToken) {
    //   return new Promise((resolve, reject) => {
    //     axiosInstance
    //       .post('Plaid/UpdateLinkToken', {
    //         AccessToken: accessToken,
    //       })
    //       .then((response) => {
    //         resolve(response);
    //         // this.updatedAccessToken = response.data;
    //       })
    //       .catch((error) => {
    //         console.error('Error updating link token', error);
    //         this.setError('Error updating link token');
    //         reject(error);
    //       });
    //   });
    // },
    updateLinkToken(itemId) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Plaid/UpdateLinkToken', {
            itemId: itemId,
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error updating link token', error);
            this.setError('Error updating link token');
            reject(error);
          });
      });
    },
    async exchangePublicToken(
      token,
      institution,
      linkedAccounts,
      isUpdateMode = false
    ) {
      try {
        const response = await axiosInstance.post('Plaid/SetAccessToken', {
          PublicToken: token,
          Institution: institution,
          Accounts: linkedAccounts,
          IsUpdateMode: isUpdateMode,
        });
        return response;
      } catch (error) {
        console.error('Error exchanging public token', error);
        this.setError('Error linking your bank account.');
        throw error;
      }
    },

    // async exchangePublicToken(token, institution, linkedAccounts) {
    //   try {
    //     const response = await axiosInstance.post('Plaid/SetAccessToken', {
    //       PublicToken: token,
    //       Institution: institution,
    //       Accounts: linkedAccounts,
    //     });
    //     return response;
    //   } catch (error) {
    //     console.log('hits error in exchange public token');
    //     console.log('error text ' + JSON.stringify(error));
    //     const isDuplicateInstitution =
    //       error?.response?.data?.message?.includes('already linked');

    //     if (isDuplicateInstitution) {
    //       console.log(
    //         'Duplicate institution detected. Launching update mode...'
    //       );
    //       await this.startUpdateMode(institution.institution_id);
    //       throw new Error('duplicate institution');
    //     } else {
    //       console.error('Error exchanging public token', error);
    //       this.setError('Error linking your bank account.');
    //       throw error;
    //     }
    //   }
    // },
    saveUpdatedAccounts(userSelectedAccounts, institution, itemId) {
      return new Promise((resolve, reject) => {
        const request = {
          accounts: userSelectedAccounts,
          institution: institution,
          itemId: itemId,
          publicToken: null,
        };

        axiosInstance
          .post(`Plaid/UpdateUserAccountsList`, request)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving updated accounts ', error);
            this.setError('Error saving updated accounts');
            reject(error);
          });
      });
    },
    async startUpdateMode(itemId) {
      try {
        const { data: updateLinkToken } = await axiosInstance.post(
          'Plaid/UpdateLinkToken',
          {
            itemId: itemId,
          }
        );
        localStorage.setItem('plaid_link_token', updateLinkToken);

        const isOAuthRedirect = window.location.href.includes('oauth_state_id');

        const onSuccess = async (publicToken, metadata) => {
          console.log('[Reconnect] === onSuccess called ===');
          console.log('[Reconnect] publicToken:', publicToken);
          console.log(
            '[Reconnect] institutionId we asked to fix:',
            itemId
          );
          try {
            // Exchange the public token — this is what refreshes the Plaid Item
            // server-side and clears ITEM_LOGIN_REQUIRED
            if (publicToken) {
              await this.exchangePublicToken(
                publicToken,
                metadata.institution,
                metadata.accounts,
                true // isUpdateMode
              );
            }

            // Reconcile account selection — handles accounts the user de-selected
            await this.saveUpdatedAccounts(
              metadata.accounts,
              metadata.institution,
              itemId
            );

            // this.clearInstitutionNeedsReconnect(institutionId);
            this.clearInstitutionNeedsReconnect(itemId);

            try {
              await this.getPlaidAccountsList();
            } catch (e) {
              console.warn(
                '[Reconnect] getPlaidAccountsList failed (non-blocking):',
                e
              );
            }

            try {
              const syncResult = await this.syncPlaidTransactions();
              if (syncResult?.requiresUpdate) {
                this.setError(
                  'Your bank still needs reconnecting. Please try again.'
                );
              }
            } catch (e) {
              console.warn(
                '[Reconnect] syncPlaidTransactions failed (non-blocking):',
                e
              );
            }
          } catch (error) {
            console.error(
              '[Reconnect] Critical error during reconnect:',
              error
            );
            this.setError("Reconnect didn't complete. Please try again.");
          } finally {
            removePlaidLinkToken();
          }
        };

        const onExit = (error, metadata) => {
          console.log('hits onexit');
          console.log('error: ', error);
          console.log('metadata: ', metadata);
          console.log(metadata.status == 'requires_oauth');
          removePlaidLinkToken();
        };

        const onEvent = (eventName, metadata) => {
          console.log('Plaid event:', eventName, metadata);
        };

        const removePlaidLinkToken = () => {
          if (localStorage.getItem('plaid_link_token')) {
            localStorage.removeItem('plaid_link_token');
          }
        };

        const handler = window.Plaid.create({
          token: updateLinkToken,
          ...(isOAuthRedirect && { receivedRedirectUri: window.location.href }),
          onSuccess,
          onEvent,
          onExit,
        });
        handler.open();
      } catch (err) {
        console.error('Error during update mode', err);
        this.setError('We couldn’t reconnect your account automatically.');
      }
    },
    getTransactions(request) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Transaction/GetTransactions`, request)
          .then((response) => {
            resolve(response);
            this.transactionsList = response.data;
          })
          .catch((error) => {
            console.error('Error getting transactions', error);
            this.setError('Error getting transactions');
            reject(error);
          });
      });
    },
    syncPlaidTransactions() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Plaid/SyncPlaidTransactions')
          .then((response) => resolve(response))
          .catch((error) => {
            if (this.handlePlaidErrorIfNeeded(error)) {
              resolve({ data: null, requiresUpdate: true });
              return;
            }
            console.error('Error syncing plaid transactions', error);
            this.setError('Error syncing plaid transactions');
            reject(error);
          });
      });
    },
    // syncPlaidTransactions() {
    //   return new Promise((resolve, reject) => {
    //     axiosInstance
    //       .post('Plaid/SyncPlaidTransactions')
    //       .then((response) => {
    //         resolve(response);
    //       })
    //       .catch((error) => {
    //         const status = error.response?.status;
    //         const errorCode = error.response?.data?.errorCode;
    //         const institutionId = error.response?.data?.institutionId;
    //         const institutionName = error.response?.data?.institutionName;

    //         console.log('[SyncPlaid] error status:', status);
    //         console.log('[SyncPlaid] errorCode:', errorCode);
    //         console.log('[SyncPlaid] institutionId:', institutionId);

    //         if (status === 401 && errorCode === 'ITEM_LOGIN_REQUIRED') {
    //           if (institutionId) {
    //             this.plaidNeedsAttention = true;
    //             this.plaidNeedsAttentionInstitution = {
    //               institutionId,
    //               institutionName: institutionName || 'your bank',
    //             };
    //           } else {
    //             this.setError('Please reconnect one of your accounts manually.');
    //           }
    //           // Resolve so the post-login flow continues — user is still authenticated
    //           resolve({ data: null, requiresUpdate: true });
    //           return;
    //         }

    //         console.error('Error syncing plaid transactions', error);
    //         this.setError('Error syncing plaid transactions');
    //         reject(error);
    //       });
    //   });
    // },
    // syncPlaidTransactions() {
    //   return new Promise((resolve, reject) => {
    //     axiosInstance
    //       .post('Plaid/SyncPlaidTransactions')
    //       .then((response) => {
    //         resolve(response);
    //       })
    //       .catch((error) => {
    //         console.log('error ' + JSON.stringify(error));
    //         if (
    //           error.response?.status === 401 &&
    //           error.response?.data?.errorCode === 'ITEM_LOGIN_REQUIRED'
    //         ) {
    //           const institutionId = error.response?.data?.institutionId;
    //           if (institutionId) {
    //             this.startUpdateMode(institutionId);
    //           } else {
    //             this.setError(
    //               'Please reconnect one of your accounts manually.'
    //             );
    //           }
    //           return;
    //         }
    //         console.error('Error syncing plaid transactions', error);
    //         this.setError('Error syncing plaid transactions');
    //         reject(error);
    //       });
    //   });
    // },
    updateTransaction(updatedTransaction) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Transaction/UpdateTransaction`, updatedTransaction)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error updating transactions', error);
            this.setError('Error updating transactions');
            reject(error);
          });
      });
    },
    createTransaction(newTransaction) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post(`Transaction/CreateTransaction`, newTransaction)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error creating transactions', error);
            this.setError('Error creating transactions');
            reject(error);
          });
      });
    },
    deleteTransaction(transactionID) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Transaction/DeleteTransaction', {
            userTransactionID: transactionID,
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error deleting transaction', error);
            this.setError('Error deleting transaction');
            reject(error);
          });
      });
    },
    getTransactionData(userTransactionID) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get(`Transaction/GetTransactionData/${userTransactionID}`)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting transaction data', error);
            this.setError('Error getting transaction data');
            reject(error);
          });
      });
    },
    getUserDashboard() {
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Dashboard/GetUserDashboard', {
            params: { startDate, endDate },
          })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting dashboard information', error);
            this.setError('Error getting dashboard information');
            reject(error);
          });
      });
    },
    getPlaidAccountsList() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Plaid/GetAccountsList')
          .then((response) => {
            this.accountsList = response.data;
            resolve(response);
          })
          .catch((error) => {
            if (this.handlePlaidErrorIfNeeded(error)) {
              resolve({ data: [], requiresUpdate: true });
              return;
            }
            console.error('Error getting plaid accounts list', error);
            this.setError('Error getting plaid accounts list');
            reject(error);
          });
      });
    },
    // getPlaidAccountsList() {
    //   return new Promise((resolve, reject) => {
    //     axiosInstance
    //       .get('Plaid/GetAccountsList')
    //       .then((response) => {
    //         resolve(response);
    //         this.accountsList = response.data;
    //       })
    //       .catch((error) => {
    //         console.error('Error getting plaid accounts list', error);
    //         this.setError('Error getting plaid accounts list');
    //         reject(error);
    //       });
    //   });
    // },
    getNetWorth() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Account/GetNetWorth')
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting net worth', error);
            this.setError('Error getting net worth');
            reject(error);
          });
      });
    },
    getNetWorthDataPoints() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Account/GetNetWorthDataPoints')
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error getting net worth data points', error);
            this.setError('Error getting net worth data points');
            reject(error);
          });
      });
    },
    captureNetWorth() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .get('Account/CaptureNetWorth')
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error capturing net worth', error);
            this.setError('Error capturing net worth');
            reject(error);
          });
      });
    },
    saveCustomAccount(accountData) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Account/SaveCustomAccount', accountData)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving the account', error);
            this.setError('Error saving the account');
            reject(error);
          });
      });
    },
    removeAccount(accountData) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('Account/DeleteAccount', accountData)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error removing the account', error);
            this.setError('Error removing the account');
            reject(error);
          });
      });
    },
    getUserAccountData() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('User/GetUserAccount', this.userInfo)
          .then(async (response) => {
            this.user = response.data;
            this.setUserAgeOver18(response.data.dateOfBirth);

            // if (Capacitor?.getPlatform && Capacitor.getPlatform() === 'ios') {
            //   try {
            //     alert('userInfo.sub value: ' + this.userInfo.sub);
            //     const info = await Purchases.getCustomerInfo();
            //     alert('Current ID before logging in: ' + info.customerInfo.originalAppUserId);
            //     const result = await Purchases.logIn({ appUserID: this.userInfo.sub });
            //     // alert('login result: ' + JSON.stringify(result));
            //   } catch (e) {
            //     alert('logIn FAILED: ' + e.message);
            //     console.error('RevenueCat login error:', e);
            //   }
            // }

            resolve(response.data);
          })
          .catch((error) => {
            const response = error.response;

            if (response && response.data && response.data.isDeletedUser) {
              this.deletedUserMessage = response.data.message;
              reject({
                isDeletedUser: true,
                message: response.data.message,
              });
            } else {
              console.error('Error getting user account data', error);
              this.setError('Error getting user account data');
              reject(error);
            }
          });
      });
    },
    saveUserAccount(userData) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('User/SaveUserAccount', userData)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving user account', error);
            this.setError('Error saving user account');
            reject(error);
          });
      });
    },
    deleteUserAccount() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .delete('User/DeleteUserAccount')
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            console.error('Error deleting user account', error);
            this.setError('Error deleting user account');
            reject(error);
          });
      });
    },
    reinstateUserAccount() {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('User/ReinstateAccount')
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            console.error('Error reinstating user account:', error);
            this.setError('Failed to reinstate account.');
            reject(error);
          });
      });
    },
    updateUserSettings(userSettings) {
      return new Promise((resolve, reject) => {
        axiosInstance
          .post('User/SaveUserSettings', userSettings)
          .then((response) => {
            this.user.settings = response.data;
            resolve(response);
          })
          .catch((error) => {
            console.error('Error saving user settings', error);
            this.setError('Error saving user settings');
            reject(error);
          });
      });
    },
    async startSubscription(payload) {
      try {
        const response = await axiosInstance.post(
          'payment/create-subscription',
          {
            PriceId: payload.priceId,
            Name: payload.name,
            PaymentMethodId: payload.paymentMethodId,
            Email: payload.email,
            PromoCode: payload.promoCode,
          }
        );
        return response.data;
      } catch (error) {
        this.setError(error.response?.data?.error || 'Subscription failed');
        throw error;
      }
    },
    async cancelSubscription() {
      try {
        const response = await axiosInstance.post(
          'payment/cancel-subscription',
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        return response;
      } catch (error) {
        this.setError(
          error.response?.data?.error || 'Subscription cancel failed'
        );
        throw error;
      }
    },
    async validatePromoCode(code) {
      try {
        const response = await axiosInstance.post(
          'payment/validate-promo-code',
          {
            Code: code,
          }
        );
        return response.data;
      } catch (e) {
        console.error('Promo validation error:', e);
        return { valid: false };
      }
    },
    // async validatePromoCode(code) {
    //   console.log('hits store call to validate promo code');
    //   try {
    //     const response = await axiosInstance.get('payment/validate-promo-code', {
    //       params: { code },
    //     });
    //     console.log('resonse ' + JSON.stringify(response));
    //     return response.data;
    //   } catch (e) {
    //     console.error('Promo validation error:', e);
    //     return { valid: false };
    //   }
    // },
    async cancelIapSubscription() {
      try {
        const response = await axiosInstance.post(
          'payment/cancel-iap-subscription'
        );
        return response;
      } catch (error) {
        this.setError(
          error.response?.data?.error || 'IAP subscription cancel failed'
        );
        throw error;
      }
    },
    async activateIapSubscription() {
      try {
        const response = await axiosInstance.post(
          'payment/activate-iap-subscription'
        );
        return response.data;
      } catch (error) {
        this.setError(error.response?.data?.error || 'IAP activation failed');
        throw error;
      }
    },

    async deactivateIapSubscription() {
      try {
        const response = await axiosInstance.post(
          'payment/deactivate-iap-subscription'
        );
        return response.data;
      } catch (error) {
        this.setError(error.response?.data?.error || 'IAP deactivation failed');
        throw error;
      }
    },
    async recategorizeTransactions(payload) {
      try {
        const response = await axiosInstance.post(
          '/Transaction/BulkRecategorizeTransactions',
          payload
        );
        return response.data.success;
      } catch (error) {
        console.error('Error recategorizing transactions:', error);
        return false;
      }
    },
    async splitTransaction(payload) {
      try {
        const response = await axiosInstance.post(
          'Transaction/SplitTransaction',
          payload
        );
        return response;
      } catch (error) {
        console.error('Error splitting transaction:', error);
        const serverMsg = error?.response?.data?.errorMessage;
        if (serverMsg) {
          this.setError(serverMsg);
        } else {
          this.setError('Error splitting transaction');
        }
        return error?.response || { status: 500 };
      }
    },
    async fullLogout(logoutFn, router, isNative, bundleId, domain) {
      console.log('[Logout] 1. fullLogout start');

      try {
        // Step 1: Try to capture net worth (non-critical, with 3s timeout)
        try {
          console.log('[Logout] 2. attempting captureNetWorth (3s timeout)');
          await Promise.race([
            this.captureNetWorth(),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('captureNetWorth timeout')),
                3000
              )
            ),
          ]);
          console.log('[Logout] 3. captureNetWorth done');
        } catch (e) {
          console.warn(
            '[Logout] 3. captureNetWorth failed/timed out (continuing):',
            e?.message || e
          );
        }

        // Step 2: Clear local state immediately — before any Auth0 calls
        console.log('[Logout] 4. clearing local state');
        this.updateUserLoggedIn(false);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('userLoggedIn');
        sessionStorage.removeItem('auth0_handling');
        sessionStorage.removeItem('auth0_fresh_login');
        this.$reset();
        console.log('[Logout] 5. local state cleared');

        if (isNative) {
          // Step 3: RevenueCat logout (non-critical, with 3s timeout)
          try {
            console.log('[Logout] 6. attempting RC logout (3s timeout)');
            await Promise.race([
              Purchases.logOut(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('RC logout timeout')), 3000)
              ),
            ]);
            console.log('[Logout] 7. RC logout done');
          } catch (e) {
            console.warn(
              '[Logout] 7. RC logout failed/timed out (continuing):',
              e?.message || e
            );
          }

          // Step 4: Trigger Auth0 logout — fire and forget, callback handles cleanup
          console.log('[Logout] 8. setting logout_in_progress flag');
          sessionStorage.setItem('logout_in_progress', '1');

          const logoutReturnTo = `${bundleId}://${domain}/ios/${bundleId}/callback`;
          console.log(
            '[Logout] 9. calling logoutFn with returnTo:',
            logoutReturnTo
          );

          try {
            // Don't await — logoutFn may not resolve until the callback returns,
            // which is handled by appUrlOpen
            logoutFn({
              logoutParams: { returnTo: logoutReturnTo },
              openUrl: async (url) => {
                console.log('[Logout] 10. opening browser:', url);
                try {
                  const { Browser } = await import('@capacitor/browser');
                  await Browser.open({ url });
                  console.log('[Logout] 11. browser opened');
                } catch (e) {
                  console.error('[Logout] 11. browser.open failed:', e);
                }
              },
            });
            console.log('[Logout] 12. logoutFn invoked (not awaited)');
          } catch (e) {
            console.error('[Logout] 12. logoutFn threw synchronously:', e);
            // Fallback: force navigate to signin
            sessionStorage.removeItem('logout_in_progress');
            window.location.href = '/';
          }

          // Native logout relies on appUrlOpen callback to complete cleanup
          return;
        } else {
          // Web logout — straightforward
          console.log('[Logout] 6w. web logout');
          sessionStorage.clear();

          try {
            await Promise.race([
              logoutFn({
                logoutParams: { returnTo: window.location.origin },
              }),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('web logout timeout')), 5000)
              ),
            ]);
            console.log('[Logout] 7w. web logout done');
          } catch (e) {
            console.warn(
              '[Logout] 7w. web logout failed/timed out:',
              e?.message || e
            );
          }

          router.replace({ name: 'SignIn' });
        }
      } catch (e) {
        console.error('[Logout] CAUGHT TOP-LEVEL ERROR:', e);
        // Nuclear option: ensure clean state regardless of what failed
        try {
          this.updateUserLoggedIn(false);
          localStorage.clear();
          sessionStorage.clear();
          this.$reset();
        } catch (cleanupErr) {
          console.error('[Logout] cleanup also failed:', cleanupErr);
        }
        window.location.href = '/';
      }
    },
    async getSplitChildren(parentTransactionID) {
      try {
        const response = await axiosInstance.get(
          `Transaction/GetSplitChildren/${parentTransactionID}`
        );
        return response;
      } catch (error) {
        console.error('Error getting split children:', error);
        return error?.response || { status: 500 };
      }
    },

    async unsplitTransaction(parentTransactionID) {
      try {
        const response = await axiosInstance.post(
          `Transaction/UnsplitTransaction/${parentTransactionID}`
        );
        return response;
      } catch (error) {
        console.error('Error unsplitting transaction:', error);
        const serverMsg = error?.response?.data?.errorMessage;
        if (serverMsg) {
          this.setError(serverMsg);
        } else {
          this.setError('Error unsplitting transaction');
        }
        return error?.response || { status: 500 };
      }
    },
    // flagInstitutionNeedsReconnect({ institutionId, institutionName }) {
    //   if (!institutionId) return;
    //   this.plaidNeedsAttention = {
    //     ...this.plaidNeedsAttention,
    //     [institutionId]: {
    //       institutionId,
    //       institutionName: institutionName || 'your bank',
    //     },
    //   };
    // },
    flagInstitutionNeedsReconnect({ itemId, institutionName }) {
      if (!itemId) return;
      this.plaidNeedsAttention = {
        ...this.plaidNeedsAttention,
        [itemId]: {
          itemId,
          institutionName: institutionName || 'your bank',
        },
      };
    },

    // clearInstitutionNeedsReconnect(institutionId) {
    //   if (!institutionId) return;
    //   const next = { ...this.plaidNeedsAttention };
    //   delete next[institutionId];
    //   this.plaidNeedsAttention = next;
    // },
    clearInstitutionNeedsReconnect(itemId) {
      if (!itemId) return;
      const next = { ...this.plaidNeedsAttention };
      delete next[itemId];
      this.plaidNeedsAttention = next;
    },

    clearAllPlaidNeedsAttention() {
      this.plaidNeedsAttention = {};
    },

    // Centralized handler for Plaid errors from any API call.
    // Returns true if it was an ITEM_LOGIN_REQUIRED that we handled,
    // false otherwise (caller should treat as a normal error).
    //commented out on jul 22nd to replace with itemId from plaid
    // handlePlaidErrorIfNeeded(error) {
    //   const status = error?.response?.status;
    //   const errorCode = error?.response?.data?.errorCode;
    //   const institutionId = error?.response?.data?.institutionId;
    //   const institutionName = error?.response?.data?.institutionName;

    //   if (status === 401 && errorCode === 'ITEM_LOGIN_REQUIRED') {
    //     if (institutionId) {
    //       this.flagInstitutionNeedsReconnect({
    //         institutionId,
    //         institutionName,
    //       });
    //     } else {
    //       this.setError('Please reconnect one of your accounts manually.');
    //     }
    //     return true;
    //   }
    //   return false;
    // },
        // Centralized handler for Plaid errors from any API call.
    // Returns true if it was an ITEM_LOGIN_REQUIRED that we handled,
    // false otherwise (caller should treat as a normal error).
    handlePlaidErrorIfNeeded(error) {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.errorCode;
      const itemId = error?.response?.data?.itemId;
      const institutionName = error?.response?.data?.institutionName;

      if (status === 401 && errorCode === 'ITEM_LOGIN_REQUIRED') {
        if (itemId) {
          console.log('item ID ' + itemId + 'inside handleplaiderror')
          this.flagInstitutionNeedsReconnect({ itemId, institutionName });
        } else {
          this.setError('Please reconnect one of your accounts manually.');
        }
        return true;
      }
      return false;
    }
    // async fullLogout(logoutFn, router, isNative, bundleId, domain) {
    //   try {
    //     try {
    //       await this.captureNetWorth();
    //     } catch (e) {
    //       console.warn('captureNetWorth failed (continuing):', e);
    //     }

    //     // ALWAYS clear local state FIRST — before any async Auth0 calls
    //     this.updateUserLoggedIn(false);
    //     localStorage.removeItem('authToken');
    //     localStorage.removeItem('userInfo');
    //     localStorage.removeItem('userLoggedIn');
    //     sessionStorage.removeItem('auth0_handling');
    //     sessionStorage.removeItem('auth0_fresh_login');
    //     this.$reset();

    //     if (isNative) {
    //       try {
    //         await Purchases.logOut();
    //       } catch (e) {
    //         console.warn('RevenueCat logout failed (continuing):', e);
    //       }

    //       sessionStorage.setItem('logout_in_progress', '1');

    //       const logoutReturnTo = `${bundleId}://${domain}/ios/${bundleId}/callback`;

    //       logoutFn({
    //         logoutParams: { returnTo: logoutReturnTo },
    //         openUrl: async (url) => {
    //           const { Browser } = await import('@capacitor/browser');
    //           await Browser.open({ url });
    //         },
    //       });

    //       // Native logout relies on callback — if it fails, at least storage is clear
    //       return;
    //     } else {
    //       // Web logout
    //       sessionStorage.clear();
    //       await logoutFn({
    //         logoutParams: { returnTo: window.location.origin },
    //       });
    //       router.replace({ name: 'SignIn' });
    //     }
    //   } catch (e) {
    //     console.error('Logout error:', e);
    //     // Nuclear option: ensure clean state even on error
    //     this.updateUserLoggedIn(false);
    //     localStorage.clear();
    //     sessionStorage.clear();
    //     this.$reset();
    //     window.location.href = '/signin';
    //   }
    // },
    // async fullLogout(logoutFn, router, isNative, bundleId, domain) {
    //   try {
    //     try {
    //       await this.captureNetWorth();
    //     } catch (e) {
    //       console.warn('captureNetWorth failed (continuing):', e);
    //     }

    //     if (isNative) {
    //       try {
    //         await Purchases.logOut();
    //         console.log('RevenueCat logout successful');
    //       } catch (e) {
    //         console.warn('RevenueCat logout failed (continuing):', e);
    //       }

    //       // Set flag BEFORE anything else
    //       sessionStorage.setItem('logout_in_progress', '1');
    //       this.updateUserLoggedIn(false);

    //       // DON'T clear localStorage or sessionStorage here!
    //       // appUrlOpen will handle cleanup after the redirect returns

    //       const logoutReturnTo = `${bundleId}://${domain}/ios/${bundleId}/callback`;

    //       logoutFn({
    //         logoutParams: { returnTo: logoutReturnTo },
    //         openUrl: async (url) => {
    //           console.log('[Logout] Opening browser:', url);
    //           const { Browser } = await import('@capacitor/browser');
    //           await Browser.open({ url });
    //         },
    //       });

    //       return;
    //     } else {
    //       // Web logout - can clear storage here since no redirect callback
    //       this.updateUserLoggedIn(false);
    //       localStorage.clear();
    //       sessionStorage.clear();
    //       this.$reset();
    //       await logoutFn({
    //         logoutParams: { returnTo: window.location.origin },
    //       });
    //     }
    //   } catch (e) {
    //     console.error('Logout error:', e);
    //     this.updateUserLoggedIn(false);
    //     localStorage.clear();
    //     sessionStorage.clear();
    //     this.$reset();
    //     router.replace({ name: 'SignIn' });
    //   }
    // }
  },
});
